import React, { useState, useEffect, useRef, useCallback } from 'react';
import jsQR from 'jsqr';
import { api } from '../services/api';
import { sound } from '../utils/audio';
import {
  Camera,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  SwitchCamera,
  Flashlight,
  Image as ImageIcon,
  RotateCcw,
  Layers,
  Volume2,
  VolumeX,
  Vibrate,
  ShieldAlert,
  ExternalLink,
  Zap,
  MapPin,
  ChevronDown,
} from 'lucide-react';

export const FESTIVAL_VENUES = [
  'Salle Royale',
  'Salle des ministres',
  'Salle des ambassadeurs',
  'Théâtre Meydene',
  'Cinéma Colisée',
  'Musée YVES SAINT LAURENT',
];

interface ScannerViewProps {
  onScanCompleted?: () => void;
}

interface ScanOutcome {
  id: string;
  token: string;
  timestamp: string;
  result: 'VALID' | 'INVALID_QR' | 'BADGE_DISABLED' | 'ALREADY_SCANNED';
  message: string;
  location?: string;
  participant?: { fullName: string; badgeNumber: string };
  badgeType?: { name: string };
  previousScanTime?: string;
}

export const ScannerView: React.FC<ScannerViewProps> = ({ onScanCompleted }) => {
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

  // Active Location / Gate selection
  const [selectedLocation, setSelectedLocation] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('passgate_selected_venue') || 'Salle Royale';
    }
    return 'Salle Royale';
  });

  const handleLocationChange = (newVenue: string) => {
    setSelectedLocation(newVenue);
    if (typeof window !== 'undefined') {
      localStorage.setItem('passgate_selected_venue', newVenue);
    }
  };

  // HTTPS / Insecure Context Detection
  const [isInsecureHttp, setIsInsecureHttp] = useState(false);

  // Controls & Settings
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [batchMode, setBatchMode] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [vibrateEnabled, setVibrateEnabled] = useState(true);
  const [isPausedAfterScan, setIsPausedAfterScan] = useState(false);

  // Instant Green Flash & QR Bounding Box
  const [isGreenFlash, setIsGreenFlash] = useState(false);

  // Manual / Token Fallback Input
  const [manualToken, setManualToken] = useState('');
  const [validating, setValidating] = useState(false);

  // Scan Results
  const [lastResult, setLastResult] = useState<ScanOutcome | null>(null);
  const [batchScans, setBatchScans] = useState<ScanOutcome[]>([]);

  // Sample badges for instant 1-click test
  const [sampleTokens, setSampleTokens] = useState<{ label: string; token: string }[]>([]);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const isCooldownRef = useRef(false);
  const isMountedRef = useRef(true);

  // Refs for 60FPS loop
  const batchModeRef = useRef(batchMode);
  batchModeRef.current = batchMode;
  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;
  const vibrateEnabledRef = useRef(vibrateEnabled);
  vibrateEnabledRef.current = vibrateEnabled;
  const selectedLocationRef = useRef(selectedLocation);
  selectedLocationRef.current = selectedLocation;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isHttp =
        window.location.protocol === 'http:' &&
        window.location.hostname !== 'localhost' &&
        window.location.hostname !== '127.0.0.1';
      setIsInsecureHttp(isHttp);
    }
  }, []);

  useEffect(() => {
    api
      .getParticipants()
      .then((list) => {
        if (list && list.length > 0) {
          setSampleTokens(
            list.slice(0, 3).map((p) => ({
              label: `${p.fullName} (${p.badgeTypeName})`,
              token: p.secureQrToken,
            }))
          );
        }
      })
      .catch(() => {});
  }, []);

  const drawGreenBoundingBox = useCallback(
    (
      points: { x: number; y: number }[],
      sourceWidth: number,
      sourceHeight: number
    ) => {
      const overlay = overlayCanvasRef.current;
      const video = videoRef.current;
      if (!overlay || !video || points.length < 4) return;

      const rect = video.getBoundingClientRect();
      if (overlay.width !== rect.width || overlay.height !== rect.height) {
        overlay.width = rect.width;
        overlay.height = rect.height;
      }

      const ctx = overlay.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, overlay.width, overlay.height);

      const videoRatio = sourceWidth / sourceHeight;
      const elementRatio = overlay.width / overlay.height;
      let scale = 1;
      let offsetX = 0;
      let offsetY = 0;

      if (elementRatio > videoRatio) {
        scale = overlay.width / sourceWidth;
        offsetY = (overlay.height - sourceHeight * scale) / 2;
      } else {
        scale = overlay.height / sourceHeight;
        offsetX = (overlay.width - sourceWidth * scale) / 2;
      }

      ctx.beginPath();
      points.forEach((pt, idx) => {
        const x = pt.x * scale + offsetX;
        const y = pt.y * scale + offsetY;
        if (idx === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.closePath();

      ctx.lineWidth = 4;
      ctx.strokeStyle = '#10b981';
      ctx.fillStyle = 'rgba(16, 185, 129, 0.35)';
      ctx.stroke();
      ctx.fill();
    },
    []
  );

  const clearOverlayCanvas = useCallback(() => {
    const overlay = overlayCanvasRef.current;
    if (!overlay) return;
    const ctx = overlay.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, overlay.width, overlay.height);
  }, []);

  const stopCameraStream = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setTorchOn(false);
    setTorchSupported(false);
    setIsCameraActive(false);
  }, []);

  const handleDetectedToken = useCallback(
    async (rawCode: string) => {
      if (isCooldownRef.current || !rawCode) return;
      isCooldownRef.current = true;

      let token = rawCode.trim();
      if (token.includes('/q/')) {
        token = token.split('/q/')[1].split('?')[0].split('#')[0].replace(/\/+$/, '');
      } else if (token.includes('token=')) {
        const match = token.match(/token=([^&#]+)/);
        if (match) token = match[1];
      }

      // Flash green & play chime
      setIsGreenFlash(true);
      if (soundEnabledRef.current) {
        sound.playPeep();
      }
      if (vibrateEnabledRef.current && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        try {
          navigator.vibrate(150);
        } catch {}
      }

      try {
        setValidating(true);
        const loc = selectedLocationRef.current;
        const outcome = await api.validateScan(token, loc);

        if (!isMountedRef.current) return;

        const scanRecord: ScanOutcome = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          token,
          timestamp: new Date().toISOString(),
          location: outcome.location || loc,
          ...outcome,
        };

        setLastResult(scanRecord);
        setBatchScans((prev) => [scanRecord, ...prev.slice(0, 49)]);

        if (soundEnabledRef.current) {
          if (outcome.result === 'ALREADY_SCANNED') {
            sound.playWarning();
          } else if (outcome.result !== 'VALID') {
            sound.playError();
          }
        }

        if (onScanCompleted) {
          onScanCompleted();
        }
      } catch (err: any) {
        if (!isMountedRef.current) return;
        const errorRecord: ScanOutcome = {
          id: `${Date.now()}`,
          token,
          timestamp: new Date().toISOString(),
          location: selectedLocationRef.current,
          result: 'INVALID_QR',
          message: 'Network error or invalid QR code',
        };
        setLastResult(errorRecord);
        if (soundEnabledRef.current) {
          sound.playError();
        }
      } finally {
        if (isMountedRef.current) {
          setValidating(false);
        }

        setTimeout(() => {
          if (isMountedRef.current) {
            setIsGreenFlash(false);
            clearOverlayCanvas();
          }
        }, 600);

        if (batchModeRef.current) {
          setTimeout(() => {
            isCooldownRef.current = false;
          }, 1200);
        } else {
          if (isMountedRef.current) {
            setIsPausedAfterScan(true);
          }
        }
      }
    },
    [onScanCompleted, clearOverlayCanvas]
  );

  const handleRescan = useCallback(() => {
    setIsPausedAfterScan(false);
    setIsGreenFlash(false);
    clearOverlayCanvas();
    isCooldownRef.current = false;
  }, [clearOverlayCanvas]);

  const startScanningLoop = useCallback(() => {
    const hasBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;
    let detector: any = null;

    if (hasBarcodeDetector) {
      try {
        detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
      } catch {
        detector = null;
      }
    }

    const scanFrame = async () => {
      if (!isMountedRef.current) return;

      const video = videoRef.current;
      if (
        video &&
        video.readyState >= video.HAVE_CURRENT_DATA &&
        video.videoWidth > 0 &&
        video.videoHeight > 0 &&
        !isCooldownRef.current
      ) {
        let foundText: string | null = null;

        if (detector) {
          try {
            const barcodes = await detector.detect(video);
            if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
              foundText = barcodes[0].rawValue;
              if (barcodes[0].cornerPoints && barcodes[0].cornerPoints.length >= 4) {
                drawGreenBoundingBox(
                  barcodes[0].cornerPoints,
                  video.videoWidth,
                  video.videoHeight
                );
              }
            }
          } catch {}
        }

        if (!foundText) {
          let canvas = offscreenCanvasRef.current;
          if (!canvas) {
            canvas = document.createElement('canvas');
            offscreenCanvasRef.current = canvas;
          }

          const vw = video.videoWidth;
          const vh = video.videoHeight;
          const cropSize = Math.floor(Math.min(vw, vh) * 0.7);
          const sx = Math.floor((vw - cropSize) / 2);
          const sy = Math.floor((vh - cropSize) / 2);

          if (canvas.width !== cropSize || canvas.height !== cropSize) {
            canvas.width = cropSize;
            canvas.height = cropSize;
          }

          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (ctx) {
            ctx.drawImage(video, sx, sy, cropSize, cropSize, 0, 0, cropSize, cropSize);
            const imageData = ctx.getImageData(0, 0, cropSize, cropSize);
            const qrResult = jsQR(imageData.data, imageData.width, imageData.height, {
              inversionAttempts: 'attemptBoth',
            });

            if (qrResult && qrResult.data) {
              foundText = qrResult.data;
              if (qrResult.location) {
                const adj = (pt: { x: number; y: number }) => ({
                  x: pt.x + sx,
                  y: pt.y + sy,
                });
                drawGreenBoundingBox(
                  [
                    adj(qrResult.location.topLeftCorner),
                    adj(qrResult.location.topRightCorner),
                    adj(qrResult.location.bottomRightCorner),
                    adj(qrResult.location.bottomLeftCorner),
                  ],
                  vw,
                  vh
                );
              }
            } else {
              const scale = Math.min(1, 640 / vw);
              const fw = Math.floor(vw * scale);
              const fh = Math.floor(vh * scale);

              if (canvas.width !== fw || canvas.height !== fh) {
                canvas.width = fw;
                canvas.height = fh;
              }

              ctx.drawImage(video, 0, 0, fw, fh);
              const fullImageData = ctx.getImageData(0, 0, fw, fh);
              const fullQrResult = jsQR(
                fullImageData.data,
                fullImageData.width,
                fullImageData.height,
                { inversionAttempts: 'attemptBoth' }
              );

              if (fullQrResult && fullQrResult.data) {
                foundText = fullQrResult.data;
                if (fullQrResult.location) {
                  const toVid = (pt: { x: number; y: number }) => ({
                    x: pt.x / scale,
                    y: pt.y / scale,
                  });
                  drawGreenBoundingBox(
                    [
                      toVid(fullQrResult.location.topLeftCorner),
                      toVid(fullQrResult.location.topRightCorner),
                      toVid(fullQrResult.location.bottomRightCorner),
                      toVid(fullQrResult.location.bottomLeftCorner),
                    ],
                    vw,
                    vh
                  );
                }
              }
            }
          }
        }

        if (foundText) {
          handleDetectedToken(foundText);
        }
      }

      if (isMountedRef.current) {
        animFrameRef.current = requestAnimationFrame(scanFrame);
      }
    };

    animFrameRef.current = requestAnimationFrame(scanFrame);
  }, [handleDetectedToken, drawGreenBoundingBox]);

  const startCameraStream = useCallback(
    async (facing: 'environment' | 'user', deviceIdOverride?: string) => {
      stopCameraStream();
      setCameraError(null);
      setIsPausedAfterScan(false);
      isCooldownRef.current = false;

      if (
        typeof navigator === 'undefined' ||
        !navigator.mediaDevices ||
        !navigator.mediaDevices.getUserMedia
      ) {
        if (
          typeof window !== 'undefined' &&
          window.location.protocol === 'http:' &&
          window.location.hostname !== 'localhost'
        ) {
          setCameraError(
            'HTTPS Required: Browsers disable camera access over HTTP. Open via https://'
          );
        } else {
          setCameraError('Camera API not supported in this browser.');
        }
        setIsCameraActive(false);
        return;
      }

      let stream: MediaStream | null = null;
      try {
        const videoConstraints: MediaTrackConstraints = deviceIdOverride
          ? {
              deviceId: { exact: deviceIdOverride },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            }
          : {
              facingMode: { ideal: facing },
              width: { ideal: 1280 },
              height: { ideal: 720 },
            };

        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false,
        });
      } catch (err1) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: facing },
            audio: false,
          });
        } catch (err2) {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: false,
            });
          } catch (err3: any) {
            if (!isMountedRef.current) return;
            setCameraError(err3.message || 'Unable to open camera.');
            setIsCameraActive(false);
            return;
          }
        }
      }

      if (!isMountedRef.current || !stream) {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;

      try {
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = allDevices.filter((d) => d.kind === 'videoinput');
        setDevices(videoDevices);
      } catch {}

      const track = stream.getVideoTracks()[0];
      if (track && 'getCapabilities' in track) {
        try {
          const caps = (track as any).getCapabilities();
          if (caps && caps.torch) {
            setTorchSupported(true);
          }
        } catch {}
      }

      const video = videoRef.current;
      if (video) {
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.muted = true;
        video.playsInline = true;
        video.srcObject = stream;
        video.play().catch(() => {});
      }

      setIsCameraActive(true);
      startScanningLoop();
    },
    [stopCameraStream, startScanningLoop]
  );

  const toggleTorch = async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    if (!track) return;
    try {
      const nextTorch = !torchOn;
      await (track as any).applyConstraints({
        advanced: [{ torch: nextTorch }],
      });
      setTorchOn(nextTorch);
    } catch {
      setTorchSupported(false);
    }
  };

  const toggleCamera = () => {
    if (devices.length > 1 && selectedDeviceId) {
      const currentIdx = devices.findIndex((d) => d.deviceId === selectedDeviceId);
      const nextDevice = devices[(currentIdx + 1) % devices.length];
      setSelectedDeviceId(nextDevice.deviceId);
      startCameraStream(facingMode, nextDevice.deviceId);
    } else {
      const nextFacing = facingMode === 'environment' ? 'user' : 'environment';
      setFacingMode(nextFacing);
      startCameraStream(nextFacing);
    }
  };

  const handleImageFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    isCooldownRef.current = false;
    setIsPausedAfterScan(false);

    try {
      const bitmap = await createImageBitmap(file);

      if (typeof window !== 'undefined' && 'BarcodeDetector' in window) {
        try {
          const detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
          const barcodes = await detector.detect(bitmap);
          if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
            handleDetectedToken(barcodes[0].rawValue);
            return;
          }
        } catch {}
      }

      const canvas = document.createElement('canvas');
      const maxDim = 1200;
      const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
      canvas.width = Math.floor(bitmap.width * scale);
      canvas.height = Math.floor(bitmap.height * scale);

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const qrResult = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'attemptBoth',
        });
        if (qrResult && qrResult.data) {
          handleDetectedToken(qrResult.data);
          return;
        }
      }

      if (soundEnabled) sound.playError();
      setLastResult({
        id: `${Date.now()}`,
        token: 'IMAGE_SCAN',
        timestamp: new Date().toISOString(),
        location: selectedLocation,
        result: 'INVALID_QR',
        message: 'No valid QR code found in the selected image.',
      });
    } catch {
      if (soundEnabled) sound.playError();
      setLastResult({
        id: `${Date.now()}`,
        token: 'IMAGE_SCAN',
        timestamp: new Date().toISOString(),
        location: selectedLocation,
        result: 'INVALID_QR',
        message: 'Could not read image file.',
      });
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    startCameraStream(facingMode);

    return () => {
      isMountedRef.current = false;
      stopCameraStream();
    };
  }, []);

  return (
    <div className="max-w-md mx-auto space-y-3.5">
      {/* HTTPS Warning Banner */}
      {isInsecureHttp && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl text-left space-y-2">
          <div className="flex items-start gap-2.5">
            <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-200">
              <span className="font-bold text-amber-300">HTTPS Required:</span> Browsers block camera hardware on HTTP. Open via <strong>https://</strong>.
            </div>
          </div>
          <button
            onClick={() => {
              window.location.href = `https://${window.location.host}${window.location.pathname}`;
            }}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-xl transition-colors shadow-sm"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Switch to HTTPS
          </button>
        </div>
      )}

      {/* Festival Location / Venue Selector */}
      <div className="rounded-2xl bg-gradient-to-r from-emerald-950/70 via-slate-900 to-teal-950/50 border-2 border-emerald-500/50 p-4 shadow-2xl backdrop-blur-xl space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs font-mono font-bold uppercase tracking-wider text-emerald-400">
              Active Scanning Venue / Gate
            </span>
          </div>
          <span className="text-[11px] font-mono text-slate-400">
            6 Festival Venues
          </span>
        </div>

        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-emerald-400">
            <MapPin className="w-5 h-5" />
          </div>
          <select
            value={selectedLocation}
            onChange={(e) => handleLocationChange(e.target.value)}
            className="w-full bg-slate-950 text-white font-extrabold text-sm border-2 border-emerald-500/60 rounded-xl pl-11 pr-10 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 cursor-pointer appearance-none shadow-lg transition-all"
          >
            {FESTIVAL_VENUES.map((venue) => (
              <option key={venue} value={venue} className="bg-slate-900 text-white text-sm py-1.5 font-semibold">
                {venue}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none text-emerald-400">
            <ChevronDown className="w-4 h-4" />
          </div>
        </div>
        <p className="text-[11px] font-mono text-slate-400">
          Scans will be recorded for: <strong className="text-emerald-300 font-bold">{selectedLocation}</strong>
        </p>
      </div>

      {/* Header Controls */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono font-bold text-white uppercase tracking-wider">
            CAMERA VIEWPORT
          </span>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
            {batchMode ? 'BATCH AUTO' : 'SINGLE'}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => {
              const next = !soundEnabled;
              setSoundEnabled(next);
              if (next) sound.playPeep();
            }}
            title={soundEnabled ? 'Chime ON' : 'Muted'}
            className={`p-2 rounded-xl border text-xs transition-colors ${
              soundEnabled
                ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400'
                : 'bg-slate-900 border-white/[0.08] text-slate-500'
            }`}
          >
            {soundEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
          </button>

          <button
            onClick={() => setVibrateEnabled(!vibrateEnabled)}
            title={vibrateEnabled ? 'Vibration ON' : 'Vibration OFF'}
            className={`p-2 rounded-xl border text-xs transition-colors ${
              vibrateEnabled
                ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400'
                : 'bg-slate-900 border-white/[0.08] text-slate-500'
            }`}
          >
            <Vibrate className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Main Camera Viewport Card with Instant Green Flash */}
      <div
        className={`relative p-2.5 rounded-3xl bg-slate-900 border-2 transition-all duration-150 overflow-hidden shadow-2xl ${
          isGreenFlash
            ? 'border-emerald-400 bg-emerald-950/40 shadow-[0_0_50px_rgba(16,185,129,0.85)] ring-4 ring-emerald-500/50'
            : 'border-white/[0.08]'
        }`}
      >
        <div className="relative aspect-[4/3] max-h-[360px] bg-black rounded-2xl overflow-hidden flex items-center justify-center">
          {/* Live Camera Video */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover transition-opacity duration-150 ${
              isCameraActive ? 'opacity-100' : 'opacity-0'
            }`}
          />

          {/* Green Bounding Box Overlay Canvas */}
          <canvas
            ref={overlayCanvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none z-10"
          />

          {/* Active Gate Watermark Overlay */}
          <div className="absolute top-3 left-3 z-20 flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-950/80 backdrop-blur-md border border-emerald-500/40 text-emerald-300 text-xs font-mono font-bold shadow-lg pointer-events-none">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <MapPin className="w-3 h-3 text-emerald-400" />
            <span>{selectedLocation}</span>
          </div>

          {/* Full Viewport Green Flash Wash */}
          {isGreenFlash && (
            <div className="absolute inset-0 bg-emerald-500/25 pointer-events-none z-10 animate-pulse" />
          )}

          {/* Camera Fallback if inactive */}
          {!isCameraActive && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center space-y-3 z-20 bg-[#090d16]">
              <Camera className="w-12 h-12 text-slate-600" />
              {cameraError ? (
                <div className="text-xs text-amber-300 bg-amber-950/80 p-3 rounded-xl border border-amber-500/40 max-w-xs leading-relaxed">
                  {cameraError}
                </div>
              ) : (
                <p className="text-xs text-slate-400">Opening camera stream...</p>
              )}
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  onClick={() => startCameraStream(facingMode)}
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold rounded-xl transition-colors shadow-md shadow-emerald-500/20"
                >
                  Enable Camera
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold rounded-xl border border-white/10 transition-colors"
                >
                  Scan From Image
                </button>
              </div>
            </div>
          )}

          {/* Viewfinder Target Reticle */}
          {isCameraActive && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10">
              <div
                className={`relative w-64 h-64 rounded-2xl border-2 transition-all duration-150 flex items-center justify-center ${
                  isGreenFlash
                    ? 'border-emerald-400 bg-emerald-500/25 shadow-[0_0_40px_rgba(52,211,153,0.95)] scale-105'
                    : isPausedAfterScan
                    ? 'border-amber-400/60 bg-black/40'
                    : 'border-white/30'
                }`}
              >
                {/* 4 Corner Markers */}
                <div
                  className={`absolute -top-1 -left-1 w-7 h-7 border-t-4 border-l-4 rounded-tl-lg transition-colors ${
                    isGreenFlash ? 'border-emerald-400' : 'border-emerald-400'
                  }`}
                />
                <div
                  className={`absolute -top-1 -right-1 w-7 h-7 border-t-4 border-r-4 rounded-tr-lg transition-colors ${
                    isGreenFlash ? 'border-emerald-400' : 'border-emerald-400'
                  }`}
                />
                <div
                  className={`absolute -bottom-1 -left-1 w-7 h-7 border-b-4 border-l-4 rounded-bl-lg transition-colors ${
                    isGreenFlash ? 'border-emerald-400' : 'border-emerald-400'
                  }`}
                />
                <div
                  className={`absolute -bottom-1 -right-1 w-7 h-7 border-b-4 border-r-4 rounded-br-lg transition-colors ${
                    isGreenFlash ? 'border-emerald-400' : 'border-emerald-400'
                  }`}
                />

                {/* Animated Laser Beam */}
                {!isGreenFlash && !isPausedAfterScan && (
                  <div className="w-full h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent shadow-[0_0_12px_#34d399] animate-laser-sweep absolute" />
                )}

                {/* Green Checkmark on Detection */}
                {isGreenFlash && (
                  <div className="flex flex-col items-center gap-1 animate-in zoom-in-50 duration-100">
                    <CheckCircle2 className="w-16 h-16 fill-emerald-500/40 text-emerald-300 drop-shadow-[0_0_12px_rgba(16,185,129,0.9)]" />
                    <span className="text-[11px] font-extrabold uppercase tracking-widest text-emerald-200 bg-emerald-950/90 px-2.5 py-0.5 rounded-full border border-emerald-400">
                      QR DETECTED
                    </span>
                  </div>
                )}

                {/* Paused Overlay */}
                {isPausedAfterScan && !isGreenFlash && (
                  <div className="pointer-events-auto flex flex-col items-center gap-2">
                    <button
                      onClick={handleRescan}
                      className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold rounded-xl shadow-lg flex items-center gap-1.5"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Tap to Rescan
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Top-Right Camera Controls */}
          {isCameraActive && (
            <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
              {torchSupported && (
                <button
                  onClick={toggleTorch}
                  title="Flashlight"
                  className={`p-2.5 rounded-full border backdrop-blur-md transition-all active:scale-95 ${
                    torchOn
                      ? 'bg-amber-500 text-slate-950 border-amber-300 shadow-[0_0_15px_rgba(245,158,11,0.6)]'
                      : 'bg-slate-900/80 hover:bg-slate-900 text-white border-white/20'
                  }`}
                >
                  <Flashlight className="w-4 h-4" />
                </button>
              )}

              <button
                onClick={toggleCamera}
                title="Switch Camera"
                className="p-2.5 bg-slate-900/80 hover:bg-slate-900 text-white rounded-full border border-white/20 backdrop-blur-md transition-transform active:scale-95"
              >
                <SwitchCamera className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Action Controls Bar */}
        <div className="grid grid-cols-3 gap-2 pt-2.5">
          <button
            onClick={handleRescan}
            className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold border border-white/[0.08] transition-colors active:scale-98"
          >
            <RotateCcw className="w-3.5 h-3.5 text-emerald-400" />
            <span>Rescan</span>
          </button>

          <button
            onClick={() => {
              const nextBatch = !batchMode;
              setBatchMode(nextBatch);
              if (nextBatch) {
                setIsPausedAfterScan(false);
                isCooldownRef.current = false;
              }
            }}
            className={`flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs font-semibold border transition-colors active:scale-98 ${
              batchMode
                ? 'bg-emerald-600/20 border-emerald-500/50 text-emerald-300'
                : 'bg-slate-800 hover:bg-slate-700 border-white/[0.08] text-slate-300'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>{batchMode ? 'Batch: ON' : 'Batch: OFF'}</span>
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold border border-white/[0.08] transition-colors active:scale-98"
          >
            <ImageIcon className="w-3.5 h-3.5 text-sky-400" />
            <span>From Image</span>
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageFileSelect}
            className="hidden"
          />
        </div>

        {/* Manual Token Input */}
        <div className="pt-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && manualToken.trim()) {
                  isCooldownRef.current = false;
                  handleDetectedToken(manualToken);
                  setManualToken('');
                }
              }}
              placeholder="Or paste QR token to validate..."
              className="flex-1 bg-slate-950 border border-white/[0.08] rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 font-mono focus:outline-none focus:border-emerald-500"
            />
            <button
              onClick={() => {
                if (manualToken.trim()) {
                  isCooldownRef.current = false;
                  handleDetectedToken(manualToken);
                  setManualToken('');
                }
              }}
              disabled={!manualToken.trim() || validating}
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold rounded-xl transition-colors disabled:opacity-50"
            >
              Validate
            </button>
          </div>
        </div>

        {/* 1-Click Test Badges */}
        {sampleTokens.length > 0 && (
          <div className="pt-2.5 text-left border-t border-white/[0.06] mt-2">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
              <Zap className="w-3 h-3 text-amber-400" />
              <span>Instant Test Badges (1-Click Check):</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {sampleTokens.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    isCooldownRef.current = false;
                    handleDetectedToken(s.token);
                  }}
                  className="px-2.5 py-1 rounded-lg bg-slate-950 hover:bg-slate-800 border border-white/[0.08] text-[11px] text-slate-300 hover:text-white transition-colors truncate max-w-[200px]"
                >
                  ⚡ {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Prominent Real-Time Scan Outcome Box */}
      {lastResult && (
        <div
          className={`p-4 rounded-3xl border transition-all animate-in zoom-in-95 duration-150 shadow-2xl backdrop-blur-xl ${
            lastResult.result === 'VALID'
              ? 'bg-emerald-950/80 border-emerald-500 text-emerald-100 ring-2 ring-emerald-500/30'
              : lastResult.result === 'ALREADY_SCANNED'
              ? 'bg-amber-950/80 border-amber-500 text-amber-100 ring-2 ring-amber-500/30'
              : 'bg-rose-950/80 border-rose-500 text-rose-100 ring-2 ring-rose-500/30'
          }`}
        >
          <div className="flex items-start gap-3.5">
            <div className="shrink-0 mt-0.5">
              {lastResult.result === 'VALID' && (
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 border-2 border-emerald-400 flex items-center justify-center text-emerald-400 shadow-[0_0_15px_rgba(52,211,153,0.5)]">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
              )}
              {lastResult.result === 'ALREADY_SCANNED' && (
                <div className="w-12 h-12 rounded-full bg-amber-500/20 border-2 border-amber-400 flex items-center justify-center text-amber-400">
                  <AlertTriangle className="w-7 h-7" />
                </div>
              )}
              {lastResult.result !== 'VALID' && lastResult.result !== 'ALREADY_SCANNED' && (
                <div className="w-12 h-12 rounded-full bg-rose-500/20 border-2 border-rose-400 flex items-center justify-center text-rose-400">
                  <XCircle className="w-7 h-7" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-base uppercase tracking-wide flex items-center justify-between">
                <span>
                  {lastResult.result === 'VALID'
                    ? 'ACCESS GRANTED'
                    : lastResult.result === 'ALREADY_SCANNED'
                    ? 'ALREADY SCANNED'
                    : lastResult.result === 'BADGE_DISABLED'
                    ? 'BADGE DISABLED'
                    : 'INVALID QR'}
                </span>
                {lastResult.result === 'VALID' && (
                  <span className="text-[10px] font-mono font-bold bg-emerald-500/20 px-2 py-0.5 rounded-full text-emerald-300 border border-emerald-500/40">
                    +1 CHECKED-IN
                  </span>
                )}
              </div>

              {lastResult.participant ? (
                <div className="mt-1 space-y-0.5">
                  <div className="text-lg font-bold text-white truncate">
                    {lastResult.participant.fullName}
                  </div>
                  <div className="text-xs font-semibold text-slate-300 flex items-center gap-1.5 flex-wrap">
                    <span className="px-2 py-0.5 rounded-md bg-black/40 text-emerald-300 font-mono">
                      {lastResult.badgeType?.name || 'Badge'}
                    </span>
                    <span>·</span>
                    <span className="font-mono text-slate-200">
                      {lastResult.participant.badgeNumber}
                    </span>
                    <span>·</span>
                    <span className="text-amber-300 flex items-center gap-1 font-mono text-[11px]">
                      <MapPin className="w-3 h-3 text-amber-400" />
                      {lastResult.location || selectedLocation}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-300 mt-1">{lastResult.message}</div>
              )}

              {lastResult.previousScanTime && (
                <div className="mt-2 text-[11px] font-mono text-amber-300 bg-black/50 px-2.5 py-1 rounded-lg inline-block border border-amber-500/30">
                  First scanned: {new Date(lastResult.previousScanTime).toLocaleTimeString()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Session Scans List */}
      {batchScans.length > 1 && (
        <div className="bg-slate-900/80 border border-white/[0.08] rounded-2xl p-3.5 space-y-2 backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider font-mono">
              Session Gate Scans ({batchScans.length})
            </span>
            <button
              onClick={() => setBatchScans([])}
              className="text-[11px] text-slate-500 hover:text-slate-300"
            >
              Clear List
            </button>
          </div>
          <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1">
            {batchScans.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between text-xs bg-slate-950/80 px-2.5 py-1.5 rounded-xl border border-white/[0.04]"
              >
                <div className="flex items-center gap-2 truncate">
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      item.result === 'VALID'
                        ? 'bg-emerald-400'
                        : item.result === 'ALREADY_SCANNED'
                        ? 'bg-amber-400'
                        : 'bg-rose-400'
                    }`}
                  />
                  <span className="font-medium text-white truncate">
                    {item.participant ? item.participant.fullName : item.token}
                  </span>
                  <span className="text-[10px] font-mono text-slate-400">
                    📍 {item.location}
                  </span>
                </div>
                <span className="text-[10px] font-mono text-slate-400 shrink-0 ml-2">
                  {item.result}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
